import {
  Button,
  Group,
  type MantineColor,
  type MantineSpacing,
  Tooltip,
  VisuallyHidden,
} from "@mantine/core";
import type { ReactNode } from "react";

/**
 * ModalFooterProps drive the shared modal action row: the confirm label, an
 * optional `confirmColor` for a colored (destructive or affirmative) confirm, a
 * `confirmDisabled` flag for a confirm gated on some precondition, an optional
 * `confirmDisabledReason` that both disables the confirm and explains why in a
 * tooltip (the organization's no-card gate), an optional `onConfirm`, and a
 * `pending` flag that spins the confirm so an in-flight action cannot be re-fired.
 * When `onConfirm` is omitted the confirm submits the enclosing form (the footer
 * sits inside a `<form>`); when supplied it is a plain button that calls the handler.
 * A Cancel button renders whenever both `cancelLabel` and `onCancel` are set, and an
 * optional `secondary` node pins a non-cancel action to the row's far left. The two
 * are independent: a dialog may carry either, both, or neither. An optional `mt`
 * opens extra space above the row.
 */
export interface ModalFooterProps {
  /** The dismiss button's label; pairs with `onCancel` to render a Cancel. */
  cancelLabel?: string;
  confirmColor?: MantineColor;
  confirmDisabled?: boolean;
  confirmDisabledReason?: string;
  confirmLabel: string;
  /** Extra top margin on the action row, opening space between it and the body. */
  mt?: MantineSpacing;
  /** Dismiss handler the Cancel button calls; pairs with `cancelLabel`. */
  onCancel?: () => void;
  /** When omitted the confirm button submits the enclosing form. */
  onConfirm?: () => void;
  pending?: boolean;
  /** A non-cancel secondary action pinned to the row's far left, opposite the confirm. */
  secondary?: ReactNode;
}

/**
 * ModalFooter is the shared action row every dialog ends with: the dismiss/confirm
 * cluster on the right, and — when an explicit `secondary` action is supplied — that
 * action pinned to the far left opposite it (`space-between`), so a destructive or
 * utility action (Reset/Disable a link, Remove a promo, Copy a key, Dissolve a group)
 * reads as distinct from the primary. The confirm is the colored primary; the Cancel
 * beside it dismisses through `onCancel`. The footer standardizes both the geometry and
 * the confirm-spins-while-pending affordance, so every dialog settles the same way.
 *
 * The `secondary` slot and the Cancel button are INDEPENDENT. A dialog with a
 * side action and unsaved edits needs both — the side action does something, the
 * Cancel abandons what was typed — and a footer that let one hide the other left the
 * segment-group editor with no labelled way out of a rename.
 *
 * Omitting `onConfirm` makes the confirm a submit button so a form-backed dialog
 * drives it through its `<form onSubmit>`; passing `onConfirm` makes it a plain
 * button for the mutation-owns-the-state dialogs. The confirm is `filled` (Mantine's
 * default variant) with its color per intent — the brand blue by default, "red" for
 * a destructive confirm. The Cancel button disables while `pending` so an in-flight
 * action cannot be dismissed out from under itself. When `confirmDisabledReason` is
 * set the confirm is disabled and wrapped in a tooltip carrying the reason, pointed
 * down (`position="bottom"`): the footer sits at the dialog's bottom edge, so the
 * reason reads below the button rather than crowding the dialog body above it. All
 * copy is supplied by the caller so the footer carries no localized strings of its own.
 */
export const ModalFooter = ({
  cancelLabel,
  confirmColor,
  confirmDisabled,
  confirmDisabledReason,
  confirmLabel,
  mt,
  onCancel,
  onConfirm,
  pending,
  secondary,
}: ModalFooterProps) => {
  const disabled = confirmDisabled || confirmDisabledReason !== undefined;

  const cancel =
    cancelLabel !== undefined && onCancel !== undefined ? (
      <Button disabled={pending} onClick={onCancel} type="button" variant="default">
        {cancelLabel}
      </Button>
    ) : null;

  const confirm = onConfirm ? (
    <Button color={confirmColor} disabled={disabled} loading={pending} onClick={onConfirm}>
      {confirmLabel}
    </Button>
  ) : (
    <Button color={confirmColor} disabled={disabled} loading={pending} type="submit">
      {confirmLabel}
    </Button>
  );

  return (
    // Two slots, always: the optional secondary on the left, and the dismiss/confirm
    // cluster on the right. The cluster is its own Group so `space-between` splits the
    // ROW rather than spreading Cancel and the confirm apart from each other.
    <Group justify={secondary == null ? "flex-end" : "space-between"} mt={mt}>
      {secondary}
      <Group>
        {cancel}
        {confirmDisabledReason ? (
          <Tooltip label={confirmDisabledReason} position="bottom">
            {/* A disabled Button does not bubble pointer events to its Tooltip parent,
                so the reason stays unreachable. The span shim restores the hover surface
                without changing the disabled affordance. The reason is the payment-method
                gate; the footer sits at the dialog's bottom edge, so the tooltip points
                down rather than crowding the body above.

                The same sentence is repeated for assistive technology, because the
                Tooltip only exists under a pointer and a disabled button is out of the
                tab order — so without it a keyboard user meets a dead confirm and no
                explanation. It is a live region so the reason is announced when it
                CHANGES too: the confirm goes from live to refused while the dialog is
                open, as a name is typed past its limit or a selection is emptied.

                It sits INSIDE the shim because Tooltip takes exactly one element child. */}
            <span>
              <VisuallyHidden aria-live="polite">{confirmDisabledReason}</VisuallyHidden>
              {confirm}
            </span>
          </Tooltip>
        ) : (
          confirm
        )}
      </Group>
    </Group>
  );
};
